/**
 * F43 (Track B) — admin GDPR request-tracking API client (scaffolding, D5).
 *
 * `GET /admin/gdpr-requests` lists export + delete requests with SLA metadata;
 * `process` runs today's soft-delete (delete) or marks fulfilled (export — no
 * archive builder at MVP); `reject` sets status='rejected'. The 30-day SLA
 * countdown is computed from `slaDueAt`.
 *
 * Component tests drive `gdpr.fixture.ts` (vitest alias). Production uses these
 * real `adminFetch` calls.
 */
import type {
  GdprRequest,
  GdprRequestKind,
  GdprRequestStatus,
  ProcessGdprRequestInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

export interface GdprListQuery {
  readonly kind?: GdprRequestKind | '';
  readonly status?: GdprRequestStatus | '';
  readonly limit?: number;
  /** Offset pagination (PU-09 — the route accepts kind/status/limit/offset). */
  readonly offset?: number;
}

export interface GdprListResponse {
  readonly data: readonly GdprRequest[];
  readonly meta: {
    /** Platform-wide totals (NOT filtered) — feed the Stat cards + nav badge. */
    readonly total: number;
    readonly pending: number;
    readonly fulfilled: number;
    readonly rejected: number;
    readonly nextCursor: string | null;
  };
}

interface DetailEnvelope {
  readonly data: GdprRequest;
}

export const gdprApi = {
  async list(query: GdprListQuery): Promise<GdprListResponse> {
    const qs = buildQuery({
      kind: query.kind || undefined,
      status: query.status || undefined,
      limit: query.limit,
      offset: query.offset,
    });
    return adminFetch<GdprListResponse>(`/admin/gdpr-requests${qs}`);
  },

  async getDetail(requestId: string): Promise<GdprRequest> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/gdpr-requests/${encodeURIComponent(requestId)}`,
    );
    return res.data;
  },

  async process(requestId: string, input: ProcessGdprRequestInput): Promise<GdprRequest> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/gdpr-requests/${encodeURIComponent(requestId)}/process`,
      { method: 'POST', body: input },
    );
    return res.data;
  },

  async reject(requestId: string, input: ProcessGdprRequestInput): Promise<GdprRequest> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/gdpr-requests/${encodeURIComponent(requestId)}/reject`,
      { method: 'POST', body: input },
    );
    return res.data;
  },
};
